import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { AuthActions } from 'common/auth/actions';
import { useBoundAuthActions } from 'common/auth/hooks/useBoundAuthActions';
import { getPostLoginRedirect } from 'common/auth/postLoginRedirect';
import { useDeviceHandoffActions } from 'common/deviceHandoff/hooks/useDeviceHandoffActions';
import { DELAY_OKTA_REDIRECT_TO_SHOW_HANDBACK_NOTICE_MS } from 'common/deviceHandoff/state/constants';
import {
    selectDeviceHandoffRejectionReason,
    selectDeviceHandoffState,
    selectIsSwitchingToSiteUser,
} from 'common/deviceHandoff/state/selectors';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { useHandleAuthError, useResetAuth } from 'module/auth/login/hooks';

export const useDeviceHandoffLoginSubmit = ({ onAuthComplete }: { onAuthComplete: () => void }) => {
    const authActions = useBoundAuthActions();
    const deviceHandoffState = useSelector(selectDeviceHandoffState);
    const { submitCredentials, retry } = useDeviceHandoffActions();

    const [isLoading, setIsLoading] = useState(false);
    const [submittedUsername, setSubmittedUsername] = useState('');

    const turnOffLoading = useCallback((): void => {
        setIsLoading(false);
    }, []);

    const handleSubmit = ({ username, password }: { username: string; password: string }): void => {
        CIPageStatus.dismiss();
        setSubmittedUsername(username);
        setIsLoading(true);

        const isHandoffInProgress = deviceHandoffState.phase !== 'inactive';

        if (isHandoffInProgress) {
            const isResumingFromRejection =
                (deviceHandoffState.phase === 'switch_to_respondent' ||
                    deviceHandoffState.phase === 'switch_to_siteuser') &&
                deviceHandoffState.step.type === 'rejected';

            if (isResumingFromRejection) {
                retry();
            }

            submitCredentials(username, password);
        } else {
            authActions.loginRequest(username, password);
        }
    };

    useResetAuth();

    useEffect(() => {
        if (!isLoading) {
            return;
        }

        const stillAwaitingIncomingUser =
            (deviceHandoffState.phase === 'switch_to_respondent' ||
                deviceHandoffState.phase === 'switch_to_siteuser') &&
            (deviceHandoffState.step.type === 'authenticating' || deviceHandoffState.step.type === 'activating');

        if (!stillAwaitingIncomingUser) {
            onAuthComplete();
            turnOffLoading();
        }
    }, [deviceHandoffState, isLoading, onAuthComplete, turnOffLoading]);

    useHandleAuthError({ onErrorHandleFinish: turnOffLoading, username: submittedUsername });

    return { isDeviceHandoffLoginSubmitLoading: isLoading, handleSubmit };
};

export const useShowPageStatusOnRespondentFinished = (): void => {
    const { t } = useTranslation('global');
    const isSwitchingToSiteUser = useSelector(selectIsSwitchingToSiteUser);

    useEffect(() => {
        const hasStagedSiteUserReturn = Boolean(getPostLoginRedirect());

        if (!isSwitchingToSiteUser || !hasStagedSiteUserReturn) {
            return;
        }

        CIPageStatus.success(t('switchuser.patient_activity_complete'), DELAY_OKTA_REDIRECT_TO_SHOW_HANDBACK_NOTICE_MS);
    }, [isSwitchingToSiteUser, t]);
};

export const useHandoffLoginFeedback = (): { handleAuthRejectionFeedback: () => void } => {
    const { t } = useTranslation('global');
    const dispatch = useDispatch();
    const rejectionReason = useSelector(selectDeviceHandoffRejectionReason);

    const handleAuthRejectionFeedback = useCallback((): void => {
        if (rejectionReason === 'respondentNotAuthorized') {
            CIPageStatus.error(t('switchuser.not_valid_patient_user'));
            dispatch(AuthActions.resetComplete());
        } else if (rejectionReason === 'userNotAuthorized') {
            CIPageStatus.error(t('switchuser.not_valid_second_user'));
            dispatch(AuthActions.resetComplete());
        } else if (rejectionReason === 'respondentAlreadyCompleted') {
            CIPageStatus.error(t('switchuser.patient_activity_complete'));
            dispatch(AuthActions.resetComplete());
        }
    }, [rejectionReason, dispatch, t]);

    return { handleAuthRejectionFeedback };
};
